import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["", "not_started", "in_progress", "waiting_on_client", "completed"];
const PRIORITIES = ["", "low", "medium", "high"];
const CATEGORIES = ["", "website", "crm_automation", "seo_google", "reviews", "leads", "general"];

const inputCls = "w-full rounded-xl border border-white/15 bg-white/[0.04] px-3 py-2 text-sm text-white outline-none focus:border-white/40";
const selCls = "rounded-xl border border-white/15 bg-[#0B162B] px-3 py-2 text-xs text-white outline-none";

export const AdminChecklist = ({ client }) => {
  const [tasks, setTasks] = useState(null);
  const [filters, setFilters] = useState({ status: "", priority: "", category: "", assigned: "", day: "" });
  const [openId, setOpenId] = useState(null);
  const [edit, setEdit] = useState({});

  const load = () => {
    if (!client) return;
    const params = new URLSearchParams({ client_id: client.id });
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    axios.get(`${API}/portal/admin/tasks?${params}`, { withCredentials: true })
      .then((r) => setTasks(r.data.tasks)).catch(() => setTasks([]));
  };

  useEffect(() => { load(); }, [client, filters]);

  const openEdit = (t) => {
    setOpenId(openId === t.id ? null : t.id);
    setEdit({ ...t });
  };

  const save = async () => {
    try {
      await axios.patch(`${API}/portal/admin/tasks/${edit.id}`, {
        status: edit.status, priority: edit.priority, assigned: edit.assigned,
        due_date: edit.due_date, internal_notes: edit.internal_notes,
        client_update_text: edit.client_update_text, files_links: edit.files_links,
        include_in_monthly_report: edit.include_in_monthly_report,
      }, { withCredentials: true });
      toast.success(edit.status === "completed" ? "Completed — logged to monthly report activity." : "Task updated.");
      setOpenId(null);
      load();
    } catch {
      toast.error("Save failed.");
    }
  };

  if (!client) {
    return <p className="text-sm text-slate-500" data-testid="checklist-no-client">Select a client in the Clients tab first.</p>;
  }

  return (
    <div data-testid="admin-checklist">
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <span className="font-display font-semibold text-white mr-2">{client.name}</span>
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className={selCls} data-testid="filter-status">
          {STATUSES.map((s) => <option key={s} value={s}>{s ? s.replaceAll("_", " ") : "All statuses"}</option>)}
        </select>
        <select value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })} className={selCls} data-testid="filter-priority">
          {PRIORITIES.map((p) => <option key={p} value={p}>{p || "All priorities"}</option>)}
        </select>
        <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} className={selCls} data-testid="filter-category">
          {CATEGORIES.map((c) => <option key={c} value={c}>{c ? c.replaceAll("_", " ") : "All categories"}</option>)}
        </select>
        <input placeholder="Assigned…" value={filters.assigned} onChange={(e) => setFilters({ ...filters, assigned: e.target.value })} className="rounded-xl border border-white/15 bg-white/[0.04] px-3 py-2 text-xs text-white outline-none w-28" data-testid="filter-assigned" />
        <input placeholder="Day #" type="number" min="1" max="30" value={filters.day} onChange={(e) => setFilters({ ...filters, day: e.target.value })} className="rounded-xl border border-white/15 bg-white/[0.04] px-3 py-2 text-xs text-white outline-none w-20" data-testid="filter-day" />
      </div>

      {tasks === null ? (
        <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
      ) : tasks.length === 0 ? (
        <p className="text-sm text-slate-500">No tasks match.</p>
      ) : (
        <div className="space-y-2">
          {tasks.map((t) => (
            <div key={t.id} className="rounded-2xl border border-white/10 overflow-hidden" data-testid={`task-row-${t.day}`}>
              <button onClick={() => openEdit(t)} className="w-full flex flex-wrap items-center gap-3 px-5 py-3.5 text-left hover:bg-white/[0.03] transition-colors duration-300">
                <span className="font-mono-label text-xs text-slate-500 w-8">D{t.day}</span>
                <span className="text-sm text-white flex-1 min-w-0 truncate">{t.name}</span>
                <span className="text-[11px] text-slate-500">{t.category.replaceAll("_", " ")}</span>
                <span className="text-[11px] text-slate-500">due {t.due_date}</span>
                <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${
                  t.status === "completed" ? "bg-white text-[#060D1A]" :
                  t.status === "in_progress" ? "border border-white/40 text-white" :
                  t.status === "waiting_on_client" ? "bg-white/15 text-white" : "border border-white/10 text-slate-500"
                }`}>{t.status.replaceAll("_", " ")}</span>
              </button>
              {openId === t.id && (
                <div className="border-t border-white/10 p-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`task-edit-${t.day}`}>
                  <div>
                    <label className="text-[10px] font-mono-label uppercase tracking-[0.2em] text-slate-500">Status</label>
                    <select value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} className={`${selCls} w-full mt-1`} data-testid="edit-status">
                      {STATUSES.slice(1).map((s) => <option key={s} value={s}>{s.replaceAll("_", " ")}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-mono-label uppercase tracking-[0.2em] text-slate-500">Priority</label>
                    <select value={edit.priority} onChange={(e) => setEdit({ ...edit, priority: e.target.value })} className={`${selCls} w-full mt-1`} data-testid="edit-priority">
                      {PRIORITIES.slice(1).map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-mono-label uppercase tracking-[0.2em] text-slate-500">Assigned</label>
                    <input value={edit.assigned || ""} onChange={(e) => setEdit({ ...edit, assigned: e.target.value })} className={`${inputCls} mt-1`} data-testid="edit-assigned" />
                  </div>
                  <div>
                    <label className="text-[10px] font-mono-label uppercase tracking-[0.2em] text-slate-500">Due date</label>
                    <input type="date" value={edit.due_date || ""} onChange={(e) => setEdit({ ...edit, due_date: e.target.value })} className={`${inputCls} mt-1`} data-testid="edit-due-date" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-[10px] font-mono-label uppercase tracking-[0.2em] text-slate-500">Client-facing update (what the client sees)</label>
                    <textarea rows={2} value={edit.client_update_text || ""} onChange={(e) => setEdit({ ...edit, client_update_text: e.target.value })} className={`${inputCls} mt-1 resize-none`} data-testid="edit-client-text" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-[10px] font-mono-label uppercase tracking-[0.2em] text-slate-500">Internal notes (never shown to client)</label>
                    <textarea rows={2} value={edit.internal_notes || ""} onChange={(e) => setEdit({ ...edit, internal_notes: e.target.value })} className={`${inputCls} mt-1 resize-none`} data-testid="edit-internal-notes" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-[10px] font-mono-label uppercase tracking-[0.2em] text-slate-500">Files / links (internal)</label>
                    <input value={edit.files_links || ""} onChange={(e) => setEdit({ ...edit, files_links: e.target.value })} className={`${inputCls} mt-1`} data-testid="edit-files" />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-300">
                    <input type="checkbox" checked={!!edit.include_in_monthly_report} onChange={(e) => setEdit({ ...edit, include_in_monthly_report: e.target.checked })} className="accent-white" data-testid="edit-include-report" />
                    Include in monthly report
                  </label>
                  <div className="flex justify-end">
                    <button onClick={save} data-testid="edit-save" className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-2.5 text-sm font-semibold text-[#060D1A] transition-colors duration-300 hover:bg-slate-300">
                      <Save className="h-4 w-4" /> Save
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
